import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { getDateRangeText } from '@culture/common-helpers/date';

import { useDeepEffect } from 'hooks/useDeepEffect';
import { catalogsSelectors } from 'ducks/catalogs';

import Button from 'components/Button';
import FilterModal from '../FilterModal';
import MobileSelect from '../MobileSelect';
import MobileList from '../MobileSelect/components/MobileList';

import Calendar from '../Calendar';
import ApplyAndClearBlock from '../blocks/ApplyAndClearBlock';

import styles from './styles.module.scss';

const MobileEventsControls = ({
	filters,
	onFilterChange,
	onDatesChange,
	filtersQueryParams,
	defaultFiltersValues,
	resetFilters
}) => {
	const { startDate, endDate } = filters;

	const [isModalOpened, setIsModalOpened] = useState(false);
	const [filterData, setFilterData] = useState(null);
	const [filterStatus, setFilterStatus] = useState(false);
	const [filterList, setFilterList] = useState({});
	const [filterOptions, setFilterOptions] = useState({});
	const [currentDate, setCurrentDate] = useState({ startDate, endDate });

	const filtersValues = useSelector(catalogsSelectors.selectFilterValues);
	const filtersLoading = useSelector(catalogsSelectors.selectFiltersLoading);

	const seasonsClassNameSetter = (condition) =>
		cx(styles.MobileEventsControls_SeasonButton, {
			[styles.MobileEventsControls_SeasonButton__active]: condition
		});

	const calendarTitle = useMemo(
		() =>
			getDateRangeText({ endDate, startDate }, 'd MMMM yyyy').replace(
				'–',
				' – '
			) || 'Выберите дату',
		[startDate, endDate]
	);

	useEffect(() => {
		setFilterOptions(filters);
	}, [filters]);

	const setFilter = useCallback(
		(filterName, filterValue) => {
			setFilterStatus(false);

			onFilterChange(filterName, filterValue);
			setFilterData(null);
		},
		[onFilterChange]
	);

	useDeepEffect(() => {
		setFilterList({
			locale: {
				name: 'locale',
				options: filtersValues.locales,
				currentOptions: filterOptions.locale || {},
				resetOption: {
					title: 'По всей России',
					name: 'russia',
					sysName: 'russia',
					titleGenitive: 'России'
				},
				searchable: true,
				apiParams: {
					url: 'catalogFilters/afisha/locales',
					debounce: 300,
					affectQueryParams: {
						...filtersQueryParams,
						isMuseumExhibition: true
					}
				},
				controlTitleField: 'title',
				label: 'Местоположение',
				valueField: 'sysName',
				accent: true,
				onChange: setFilter
			},
			date: {
				name: 'date',
				label: 'Календарь',
				options: [],
				customChildren: (
					<Calendar
						startDate={startDate}
						endDate={endDate}
						setNewDates={setCurrentDate}
					/>
				),
				resetOption: {
					title: calendarTitle
				},
				onChange: setFilter
			}
		});
	}, [filtersValues, setFilter, filtersQueryParams]);

	const switchFilter = (data, status) => {
		setFilterData(data);
		setFilterStatus(status);
	};

	const toggleModal = () => {
		setIsModalOpened((prev) => !prev);
	};

	const closeModal = () => {
		switchFilter(null, false);

		if (!filterData) {
			toggleModal();
		}
	};

	const closeCurrentFilter = () => {
		setFilterStatus(false);
		setFilterData(null);
	};

	const resetOneFilter = (name) => {
		const filter = filterList[name];
		const omitFilters = _(filterOptions).omit(name);

		setFilterOptions(omitFilters);
		onFilterChange(name, filter.resetOption[filter.valueField || 'value']);
	};

	const filtersEvents = {
		locale: {
			onClear: () => resetOneFilter('locale')
		},
		date: {
			onApply: () => {
				const isOneDay = !currentDate.endDate;
				const dates = isOneDay
					? { ...currentDate, endDate: currentDate.startDate }
					: currentDate;

				onDatesChange(dates);
				closeCurrentFilter();
			},
			onClear: () => {
				onDatesChange({});
				setCurrentDate({});
			}
		},
		all: {
			onApply: () => {
				closeModal();
			},
			onClear: () => {
				resetFilters();
			}
		}
	};

	const TopControlsBlock = () => (
		<div className={styles.TopControlsBlock}>
			<Button
				icon="filtersButton"
				width="square"
				className={styles.TopControlsBlock__Button}
				onClick={toggleModal}
			/>
			<div className={styles.TopControlsBlock__Title}>Фильтры</div>
		</div>
	);

	return (
		<div className={styles.MobileEventsControls}>
			<TopControlsBlock />
			<FilterModal modalStatus={isModalOpened} modalHandler={toggleModal}>
				<div className={styles.MobileEventsControls__Modal}>
					<div
						className={cx(styles.MobileEventsControls__ModalHeader, {
							[styles.MobileEventsControls__ModalHeader_withOffset]: !filterStatus
						})}
					>
						<Button
							className={styles.MobileEventsControls__FilterClose}
							icon="close"
							onClick={closeModal}
						/>
						<div className={styles.MobileEventsControls__ModalTitle}>
							{filterData?.label || 'Фильтры'}
						</div>
					</div>
					{filterStatus && !_.isEmpty(filterData) ? (
						<>
							<MobileSelect
								{...filterList[filterData.name]}
								filterOptions={filterOptions}
								selectedValue={filterOptions[filterData.name]}
								isLoading={filtersLoading}
							/>
							{filterData.name !== 'locale' && (
								<ApplyAndClearBlock {...filtersEvents[filterData.name]} />
							)}
						</>
					) : (
						<>
							<MobileList
								{...{
									filters,
									filterOptions,
									filterList,
									onFilterChange,
									onDatesChange,
									defaultFiltersValues,
									resetOneFilter,
									switchFilter,
									seasonsClassNameSetter
								}}
							/>
							<ApplyAndClearBlock {...filtersEvents.all} />
						</>
					)}
				</div>
			</FilterModal>
		</div>
	);
};

MobileEventsControls.propTypes = {
	onFilterChange: PropTypes.func,
	onDatesChange: PropTypes.func,
	onPriceChange: PropTypes.func,
	onFilterReset: PropTypes.func,
	defaultFiltersValues: PropTypes.object,
	filters: PropTypes.shape({
		season: PropTypes.string,
		locale: PropTypes.string,
		startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
		endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
		query: PropTypes.string
	}),
	filtersQueryParams: PropTypes.object,
	resetFilters: PropTypes.func
};

export default MobileEventsControls;
